import { lpad } from './fmt';
import { DateRangeID, IntervalGranularity } from '@fullstory/client-api';
import moment, { Moment } from 'moment';
import { assertExhaustive, NonNullableObject } from '@fullstory/typeutils';
import { intl } from '@fullstory/intl';
import { defineMessages } from 'react-intl';
import { differenceInDays, getHours, getMinutes, isSameDay, isSameMinute, subDays } from 'date-fns';

export const SOLR_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

enum TimeUnitInMillis {
  Year = 3.154e10,
  Month = 2.628e9,
  Week = 6.048e8,
  Day = 8.64e7,
  Hour = 3.6e6,
  Minute = 6e4,
  Second = 1e3,
}

// returns a new copy of a date
export function cloneDate(date: Date): Date {
  return new Date(date.getTime());
}

export function renderTime(ms: number, hour12: boolean = true) {
  // If hour12 is true, uses 12 hour time. If false, uses 24 hour time.
  return intl.formatDate(ms, {
    hour12,
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
}

// Determines whether the two given dates refer to the same date (regardless of time).
export function isSameDate(a: Date, b: Date): boolean {
  if (!a || !b) return false;
  return (
    a.getFullYear() == b.getFullYear() && a.getMonth() == b.getMonth() && a.getDate() == b.getDate()
  );
}

// Returns true if the date is within the start and end dates provided.
// Also returns false if any dates are undefined.
export function inRange(date: Date, start: Date, end: Date): boolean {
  if (!date || !start || !end) return false;
  return start.getTime() < date.getTime() && date.getTime() < end.getTime();
}

// Returns true if the provided date falls between the min and max date. If either limit is excluded,
// the provided date will meet that limit condition. If `inMonth` is true, then true will be returned if the
// date is within the months corresponding to min/max.
export function withinLimits(d: Date, min?: Date, max?: Date, inMonth?: boolean): boolean {
  const testMin = min && inMonth ? beginningOfMonth(min) : min;
  const testMax = max && inMonth ? endOfMonth(max) : max;
  return (
    (!testMin || d.getTime() >= testMin.getTime()) && (!testMax || d.getTime() <= testMax.getTime())
  );
}

export function numWeeksInMonth(date: Date): number {
  const firstWeek = getStartOfWeek(date);
  date.setMonth(date.getMonth() + 1);
  date.setDate(date.getDate() - 1);
  const endWeek = getStartOfWeek(date);
  return Math.round((endWeek.getTime() - firstWeek.getTime()) / TimeUnitInMillis.Day / 7) + 1;
}

export function resetDateToMidnight(date: Date): Date {
  const outDate = cloneDate(date);
  outDate.setHours(0, 0, 0, 0);
  return outDate;
}

// Clamps time to end of date (local).
export function clampToEndOfDay(date: Date) {
  date.setHours(23);
  date.setMinutes(59);
  date.setSeconds(59);
  date.setMilliseconds(999);
}

export function nextDay(date: Date): Date {
  const tempDate = cloneDate(date);
  tempDate.setDate(tempDate.getDate() + 1);
  return tempDate;
}

// Returns the previous Sunday
export function getStartOfWeek(date: Date): Date {
  const tempDate = cloneDate(date);
  tempDate.setHours(0, 0, 0, 0);
  const day = tempDate.getDay();
  if (day != 0) {
    tempDate.setDate(tempDate.getDate() - day);
  }
  return tempDate;
}

// Returns the first day in the month of the given date
export function beginningOfMonth(d: Date): Date {
  const tempDate = cloneDate(d);
  tempDate.setDate(1);
  tempDate.setHours(0, 0, 0, 0);
  return tempDate;
}

// Returns the last day in the month of the given date with the
// time set to the last millisecond of the day (i.e. 12:59:59.999 pm)
export function endOfMonth(d: Date): Date {
  const tempDate = cloneDate(d);
  tempDate.setMonth(tempDate.getMonth() + 1, 0);
  tempDate.setHours(23, 59, 59, 999);
  return tempDate;
}

export function renderTimeNickname(ms: number): string {
  // Early out if we get a bad value.
  if (ms == 0) return '---';

  const d = new Date(ms);
  return renderDateNickname(d);
}

export function renderDateNicknameNoTime(d: Date): string {
  const now = new Date();
  if (isSameDate(d, now)) {
    return intl.formatMessage({ defaultMessage: 'Today', id: 'zWgbGg' });
  }

  now.setDate(now.getDate() - 1);
  if (isSameDate(d, now)) {
    return intl.formatMessage({ defaultMessage: 'Yesterday', id: '6dIxDP' });
  }

  // Construct a regular date, possibly including the year
  return intl.formatDate(d, {
    day: 'numeric',
    month: 'long',
    year: d.getFullYear() != now.getFullYear() ? 'numeric' : undefined,
  });
}

export function renderDateNickname(d: Date): string {
  const now = new Date();
  if (isSameDate(d, now)) {
    return intl.formatMessage(
      { defaultMessage: 'Today, {timeStamp, time, short}', id: 'ey+yzj' },
      { timeStamp: d },
    );
  }

  now.setDate(now.getDate() - 1);
  if (isSameDate(d, now)) {
    return intl.formatMessage(
      { defaultMessage: 'Yesterday, {time, time, short}', id: 'WEYzpe' },
      { time: d },
    );
  }

  // Construct a regular date, possibly including the year
  return intl.formatDate(d, {
    day: 'numeric',
    month: 'long',
    year: d.getFullYear() != now.getFullYear() ? 'numeric' : undefined,
    hour: 'numeric',
    minute: 'numeric',
  });
}

export function renderShortDateNickname(d: Date): string {
  const now = new Date();
  if (isSameDate(d, now)) {
    return intl.formatTime(d);
  }

  if (d.getFullYear() < now.getFullYear()) {
    return intl.formatDate(d, { month: 'numeric', day: 'numeric', year: '2-digit' });
  } else {
    return intl.formatDate(d, { month: 'short', day: 'numeric' });
  }
}

export function renderNumericDate(d: Date): string {
  const now = new Date();

  return intl.formatDate(d, {
    day: 'numeric',
    month: 'numeric',
    year: d.getFullYear() != now.getFullYear() ? '2-digit' : undefined,
  });
}

// Used for getting a duration is HH:MM:SS format.
// Unused components are omitted from the returned string.
export function getDurationNicknameFromMs(millis: number): string {
  const seconds = Math.floor(millis / 1000);
  if (seconds < 60) {
    // seconds
    return `0:${lpad(seconds.toFixed(), 2, '0')}`;
  } else if (seconds < 3600) {
    // minutes
    const min = Math.floor(seconds / 60).toString();
    const sec = lpad((seconds % 60).toFixed(), 2, '0');
    return `${min}:${sec}`;
  } else {
    // hours
    const hours = Math.floor(seconds / 3600);
    const min = lpad(Math.floor((seconds - hours * 3600) / 60).toString(), 2, '0');
    const sec = lpad((seconds % 60).toFixed(), 2, '0');
    return `${hours.toString()}:${min}:${sec}`;
  }
}

// Takes a date string from a <input type="date" /> and returns a Date object.
// The time on the date is set to 23:59:00. So the last minute of that day.
export function dateFromDateInputVal(val: string): Date {
  const parts = val.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Yeah. fuck this.
  const date = parseInt(parts[2], 10);
  const d = new Date();

  d.setFullYear(year, month, date);
  d.setHours(23, 59, 0);
  return d;
}

// Returns duration strings suitable for mostly millisecond to second resolutions.
// Will fall back to HH:MM:SS if longer than a minute.
export function shortDurationString(duration: number): string {
  if (duration < 1000) {
    return intl.formatNumber(duration, {
      unitDisplay: 'narrow',
      unit: 'millisecond',
      style: 'unit',
    });
  } else if (duration < 60000) {
    return intl.formatNumber(duration / 1000, {
      unitDisplay: 'narrow',
      unit: 'second',
      style: 'unit',
      maximumFractionDigits: 2,
    });
  } else {
    return getDurationNicknameFromMs(duration);
  }
}

export function validMomentFromDate(input: string): moment.Moment | null {
  const DATE_FORMATS: string[] = [
    'M/D/YY',
    'M/D/YY H:mm',
    'M/D/YY H:mm a',
    'M/D/YY H:mma',
    'M/D/YYYY',
    'M/D/YYYY H:mm',
    'M/D/YYYY H:mm a',
    'M/D/YYYY H:mma',
    'YYYY-MM-DD',
    'YYYY-MM-DD H:mm',
    'YYYY-MM-DD H:mm a',
    'YYYY-MM-DD H:mma',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
  ];

  return parseMoment(input, DATE_FORMATS);
}

export function isValidDate(date: Date | undefined | null) {
  return date && !isNaN(date.getTime());
}

export function parseMoment(val: string, formats: string[]): moment.Moment | null {
  const m = moment(val, formats, true);

  return m.isValid() ? m : null;
}

export function getNiceStringFromSolrDate(dateStr: string): string | null {
  const m: moment.Moment = moment(dateStr, SOLR_DATE_FORMAT, true);
  if (!m.isValid()) {
    return null;
  }
  return getNiceStringFromMoment(m);
}

export function getNiceStringFromDate(date: Date): string {
  return intl.formatDate(date, {
    hour: 'numeric',
    minute: 'numeric',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
}

function getNiceStringFromMoment(m: moment.Moment): string {
  // Round to the nearest day if the time == 00:00:00.
  if (m.hours() == 0 && m.minutes() == 0 && m.seconds() == 0) {
    return intl.formatDate(m.toDate(), { month: 'numeric', day: 'numeric', year: 'numeric' });
  }
  return intl.formatMessage(dateFormatMessages.shortShowTime, { date: m.toDate() });
}

export interface DateRange {
  startTime: Date | null;
  endTime: Date | null;
}

export function getDateRange(id: DateRangeID, now: Date): DateRange {
  switch (id) {
    case DateRangeID.Today:
      return {
        startTime: moment(now).startOf('day').toDate(),
        endTime: moment(now).add(1, 'day').startOf('day').toDate(),
      };
    case DateRangeID.Yesterday:
      return {
        startTime: moment(now).add(-1, 'day').startOf('day').toDate(),
        endTime: moment(now).startOf('day').toDate(),
      };
    case DateRangeID.Past60Minutes: {
      const startMoment = moment(now).startOf('minute').subtract(59, 'minute');
      return {
        startTime: startMoment.clone().toDate(),
        endTime: startMoment.clone().add(60, 'minute').toDate(),
      };
    }
    case DateRangeID.Past3Hours: {
      const startMoment = moment(now).startOf('hour').subtract(2, 'hour');
      return {
        startTime: startMoment.clone().toDate(),
        endTime: startMoment.clone().add(3, 'hour').toDate(),
      };
    }
    case DateRangeID.Past6Hours: {
      const startMoment = moment(now).startOf('hour').subtract(5, 'hour');
      return {
        startTime: startMoment.clone().toDate(),
        endTime: startMoment.clone().add(6, 'hour').toDate(),
      };
    }
    case DateRangeID.Past24Hours:
      return {
        startTime: moment(now).add(-23, 'hour').startOf('hour').toDate(),
        endTime: moment(now).add(1, 'hour').startOf('hour').toDate(),
      };
    case DateRangeID.PastWeek:
      return {
        startTime: moment(now).add(-6, 'day').startOf('day').toDate(),
        endTime: moment(now).add(1, 'day').startOf('day').toDate(),
      };
    case DateRangeID.PastMonth:
      return {
        startTime: moment(now).add(-29, 'day').startOf('day').toDate(),
        endTime: moment(now).add(1, 'day').startOf('day').toDate(),
      };
    case DateRangeID.Past90Days:
      return {
        startTime: moment(now).add(-89, 'day').startOf('day').toDate(),
        endTime: moment(now).add(1, 'day').startOf('day').toDate(),
      };
    case DateRangeID.PastYear:
      return {
        startTime: moment(now).add(-364, 'day').startOf('day').toDate(),
        endTime: moment(now).add(1, 'day').startOf('day').toDate(),
      };
    case DateRangeID.Custom:
    case DateRangeID.Since:
    case DateRangeID.AllTime:
      return {
        startTime: null,
        endTime: null,
      };
    default:
      assertExhaustive(id);
  }
}

// Returns valid dates for all cases, including custom, since, and all time.
export function dateRangeToDates(
  dateRangeId: DateRangeID,
  now: Date,
  startTime: string,
  endTime: string,
): NonNullableObject<DateRange> {
  // Address all types not addressed by getDateRange, ensure non-null keys
  switch (dateRangeId) {
    case DateRangeID.Custom:
      return {
        startTime: new Date(startTime),
        endTime: new Date(endTime),
      };
    case DateRangeID.Since:
      return {
        startTime: new Date(startTime),
        endTime: moment(now).add(1, 'day').startOf('day').toDate(),
      };
    case DateRangeID.AllTime:
      return {
        startTime: new Date(0),
        endTime: moment(now).add(1, 'day').startOf('day').toDate(),
      };
    default: {
      const dateRange = getDateRange(dateRangeId, now);
      return {
        startTime: dateRange.startTime ?? new Date(startTime),
        endTime: dateRange.endTime ?? new Date(endTime),
      };
    }
  }
}

export function formatTooltipTimestampRange(
  granularity: IntervalGranularity,
  start: number,
  end: number,
): string {
  const sm = moment.unix(start);
  const em = moment.unix(end);
  switch (granularity) {
    case IntervalGranularity.MonthGranularity:
      return intl.formatDate(sm.toDate(), { month: 'short', year: 'numeric' });
    case IntervalGranularity.WeekGranularity:
      // Week spans a new year; add year to dates.
      if ((sm.month() == 11 && em.month() == 0) || (sm.month() == 0 && sm.date() == 1)) {
        return intl.formatDateTimeRange(sm.toDate(), em.toDate(), {
          weekday: 'short',
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
        });
      }
      return intl.formatDateTimeRange(sm.toDate(), em.toDate(), {
        weekday: 'short',
        month: '2-digit',
        day: '2-digit',
      });
    case IntervalGranularity.DayGranularity: {
      const now = moment(new Date().getTime());
      return intl.formatDate(sm.toDate(), {
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
        year: now.year() != sm.year() ? 'numeric' : undefined,
      });
    }
    case IntervalGranularity.HourGranularity:
      // New day; add the date to the label.
      if (sm.hours() == 0) {
        return intl.formatDateTimeRange(sm.toDate(), em.toDate(), {
          hour: '2-digit',
          day: 'numeric',
          month: '2-digit',
        });
      }
      return intl.formatDateTimeRange(sm.toDate(), em.toDate(), { hour: '2-digit' });
    default:
      throw 'invalid granularity';
  }
}

// useful for adjusting date manipulation tests for CI
export const dateToUtc = (date: Date) =>
  new Date(date.setMinutes(date.getMinutes() + date.getTimezoneOffset()));

export type DateFormattingOptions =
  | {
      format: 'verbose';
      showTime?: boolean;
      showWeekDay?: boolean;
    }
  | {
      format: 'abbreviated';
      showTime?: boolean;
      showWeekDay?: boolean;
    }
  | {
      format: 'abbreviated-date-time';
    }
  | {
      format: 'short';
      showTime?: boolean;
      hideYear?: boolean;
    }
  | {
      format: 'minimal';
      showTime?: boolean;
      hideYear?: boolean;
    }
  | {
      format: 'time-only';
    }
  | {
      format: 'month-day-time';
      hideYear?: boolean;
    }
  | {
      format: 'month-day-time-minimal';
    };

const dateFormatMessages = defineMessages({
  default: {
    defaultMessage: '{date, date, ::eeeee MMMM d, yyy} at {date, time, short}',
    id: 'ctWjcH',
  },
  verboseShowTime: {
    defaultMessage: '{date, date, ::MMMM d, yyy} at {date, time, short}',
    id: '1BptH5',
  },
  verboseShowWeekDay: { defaultMessage: '{date, date, ::eeeee MMMM d, yyy}', id: '3h0AGG' },
  abbreviatedShowTime: {
    defaultMessage: '{date, date, ::MMM d, yyy} {date, time, short}',
    id: '16DtHI',
  },
  abbreviatedBoth: {
    defaultMessage: '{date, date, ::E MMM d, yyy} {date, time, short}',
    id: 'rPZjBA',
  },
  shortShowTime: { defaultMessage: '{date,date, ::M/d/yyy} {date, time, short}', id: 'ei7w7o' },
  shortBoth: { defaultMessage: '{date,date, ::M/d} {date, time, short}', id: 'GfuizZ' },
  minimalShowTime: { defaultMessage: '{date,date, ::M/d/yy} {date, time, short}', id: 'LfV1Oc' },
  minimalBoth: { defaultMessage: '{date,date, ::M/d} {date, time, short}', id: 'GfuizZ' },
  monthDayTime: {
    defaultMessage:
      '{date, date, ::MMMM d}{hideYear, select, true {} other {, yyy}}, {date, time, short}',
    id: '3fNTGx',
  },
  monthDayTimeMinimal: {
    defaultMessage: '{date, date, ::M/d} {date, time, short}',
    id: 'g+SegI',
  },
});

// if options are not passed, defer to default format below
export const dateFormat = (date: Date, options?: DateFormattingOptions) => {
  if (!options)
    return intl.formatMessage(dateFormatMessages.default, {
      date,
    });

  switch (options.format) {
    case 'verbose':
      if (options.showWeekDay && options.showTime)
        return intl.formatMessage(dateFormatMessages.default, { date });
      if (options.showTime) return intl.formatMessage(dateFormatMessages.verboseShowTime, { date });
      if (options.showWeekDay)
        return intl.formatMessage(dateFormatMessages.verboseShowWeekDay, { date });
      return intl.formatDate(date, { month: 'long', day: 'numeric', year: 'numeric' });
    case 'abbreviated':
      if (options.showTime && options.showWeekDay)
        return intl.formatMessage(dateFormatMessages.abbreviatedBoth, { date });
      if (options.showTime)
        return intl.formatMessage(dateFormatMessages.abbreviatedShowTime, { date });
      if (options.showWeekDay)
        return intl.formatDate(date, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          weekday: 'short',
        });
      return intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
    case 'abbreviated-date-time':
      // Have to use formatMessage here to get a comma between then
      return intl.formatMessage(
        { id: 'KWZwqA', defaultMessage: '{formattedDate}, {formattedTime}' },
        {
          formattedDate: intl.formatDate(date, {
            month: 'short', // MMM (e.g., Nov)
            day: 'numeric', // d (e.g., 24)
          }),
          formattedTime: intl.formatTime(date, {
            timeStyle: 'short', // e.g., 2:34 PM
          }),
        },
      );
    case 'short':
      if (options.showTime && options.hideYear)
        return intl.formatMessage(dateFormatMessages.shortBoth, { date });
      if (options.showTime) return intl.formatMessage(dateFormatMessages.shortShowTime, { date });
      if (options.hideYear) return intl.formatDate(date, { month: 'numeric', day: 'numeric' });
      return intl.formatDate(date, { month: 'numeric', day: 'numeric', year: 'numeric' });
    case 'minimal':
      if (options.showTime && options.hideYear)
        return intl.formatMessage(dateFormatMessages.minimalBoth, { date });
      if (options.showTime) return intl.formatMessage(dateFormatMessages.minimalShowTime, { date });
      if (options.hideYear) return intl.formatDate(date, { month: '2-digit', day: '2-digit' });
      return intl.formatDate(date, { month: '2-digit', day: '2-digit', year: '2-digit' });
    case 'time-only':
      return intl.formatTime(date, { hour: 'numeric', minute: '2-digit' });
    case 'month-day-time':
      return intl.formatMessage(dateFormatMessages.monthDayTime, {
        date,
        hideYear: options.hideYear ? 'true' : 'false',
      });
    case 'month-day-time-minimal':
      return intl.formatMessage(dateFormatMessages.monthDayTimeMinimal, {
        date,
      });
    default:
      assertExhaustive(options);
  }
};

export const isMidnight = (day: Moment) =>
  day.hours() === 0 && day.minutes() === 0 && day.seconds() === 0;

/**
 * If the end date is at midnight, it actually represents the previous day due to the way that ranges
 * work in solr. Solr interprets the date and time, so if we want the end date to be 8/7 then we set the
 * end date to 8/8 at 12am so that the entire day of 8/7 is included.
 *
 * Internally shift the day back to 8/7 so that the date string reflects the intended date.
 */
export function getAdjustedEndDate(date: Date, utc?: boolean): Moment {
  const makeMoment = utc ? moment.utc : moment;
  const momentDate = makeMoment(date);
  return date && isMidnight(momentDate) ? momentDate.subtract(1, 'day') : momentDate;
}

/**
 * Returns a locale friendly relative time string (i.e "4 years ago")
 * */
export function getFormattedRelativeTime(date: Date) {
  const diff = date.getTime() - new Date().getTime();

  if (Math.abs(diff) > TimeUnitInMillis.Year) {
    return intl.formatRelativeTime(Math.round(diff / TimeUnitInMillis.Year), 'year');
  } else if (Math.abs(diff) > TimeUnitInMillis.Month)
    return intl.formatRelativeTime(Math.round(diff / TimeUnitInMillis.Month), 'month');
  else if (Math.abs(diff) > TimeUnitInMillis.Day) {
    return intl.formatRelativeTime(Math.round(diff / TimeUnitInMillis.Day), 'day');
  } else if (Math.abs(diff) > TimeUnitInMillis.Hour)
    return intl.formatRelativeTime(Math.round(diff / TimeUnitInMillis.Hour), 'hour');
  else if (Math.abs(diff) > TimeUnitInMillis.Minute) {
    return intl.formatRelativeTime(Math.round(diff / TimeUnitInMillis.Minute), 'minute');
  }

  // Anything less than a minute is an unspecific message
  if (diff <= 0) return intl.formatMessage({ defaultMessage: 'a few seconds ago', id: 'aC/wxa' });
  else return intl.formatMessage({ defaultMessage: 'in a few seconds', id: 'aMWVqB' });
}

/**
 * If we are not showing data by hour, and the time is midnight, we can omit the 12:00
 *
 * eg. 12:00 AM July 4 to 12:00 AM July 11 can just be July 4 - July 11
 */
function canHideTime(start: Date, end: Date) {
  return (
    getHours(start) === 0 && getMinutes(start) === 0 && getHours(end) === 0 && getMinutes(end) === 0
  );
}

/**
 * A day is always a 24 hour period with a start and an end. Most of the time,
 * we can simplify this day to a single date (eg. July 4) but sometimes we need
 * to be explicit about the start and end in case a user from another timezone
 * is viewing data categorized by this 'day'.
 *
 * eg. July 4 in EST would be July 3 @ 10:00pm - July 4 @ 10:00pm in MST
 */
function canSimplifyDay(start: Date, end: Date) {
  return canHideTime(start, end) && differenceInDays(end, start) === 1;
}

interface FormatDateRangeProps {
  startDate: Date;
  endDate: Date;
  alwaysShowTime?: boolean;
}

export function formatDateRange({
  startDate,
  endDate,
  alwaysShowTime = false,
}: FormatDateRangeProps) {
  // start and end times are equal
  if (isSameDay(startDate, endDate) && isSameMinute(startDate, endDate)) {
    return dateFormat(startDate, { format: 'abbreviated' });
  }

  // Can omit the end date and times here because this is just one day midnight
  // to midnight (eg. July 4 at 12:00 to July 5 at 12:00 should just be "July
  // 4")
  if (canSimplifyDay(startDate, endDate) && !alwaysShowTime) {
    return dateFormat(startDate, { format: 'abbreviated' });
  }

  // If this is a multi day range but still midnight to midnight, we can hide
  // the times but in this case we must also render the end date as one day
  // before the end date because our date ranges without time stamps are
  // inclusive. (eg. 12:00 am July 6 - 12:00 am July 13 should be "July 6 - July 12")
  if (canHideTime(startDate, endDate) && !alwaysShowTime) {
    return intl.formatMessage(
      { defaultMessage: '{startDate} – {endDate}', id: '5Rl2zb' },
      {
        startDate: dateFormat(startDate, { format: 'abbreviated' }),
        endDate: dateFormat(subDays(endDate, 1), { format: 'abbreviated' }),
      },
    );
  }

  return intl.formatMessage(
    {
      defaultMessage:
        '{startHour}{isSameDay, select, true {} other { {startDay}}} – {endHour} {endDay}',
      id: 'qEFqqs',
    },
    {
      startHour: dateFormat(startDate, { format: 'time-only' }),
      endHour: dateFormat(endDate, { format: 'time-only' }),
      startDay: dateFormat(startDate, { format: 'abbreviated' }),
      endDay: dateFormat(endDate, { format: 'abbreviated' }),
      isSameDay: isSameDay(startDate, endDate),
    },
  );
}
